/**
 * @file PWA support — service worker registration, install prompt capture,
 * version polling, and reactive state for `<PwaButton>`.
 *
 * Mirrors the split used elsewhere in airlift (`initDb`/`useDb`,
 * `initUser`/`useUser`): `initPwa()` runs once at startup and `usePwa()`
 * exposes a reactive object usable from any component.
 *
 * Version model (see specs/pwa-support-spec.md "Running vs available"):
 *  - Running (`currentVersion`/`currentCommit`) is read once at startup
 *    from a plain `version.conf` fetch. When a SW controls the page,
 *    that fetch resolves from the precache, so the value reflects the
 *    bundle the user is actually executing — and persists across
 *    sessions for free, because the cache does.
 *  - Available (`availableVersion`/`availableCommit`) is updated by the
 *    live poller using `version.conf?no_cache=1`. The query string
 *    bypasses the SW cache via standard exact-URL matching (the SW does
 *    not need a version-specific branch).
 *  - `updateAvailable` flips when the two commits diverge.
 *
 * The SW is fully optional from the page's perspective. Version display
 * goes through `version.conf` regardless of whether a SW is present;
 * postMessage is used only as a *command* channel (SKIP_WAITING), never
 * to query the running version.
 */

const { reactive, computed } = Vue;
import { useSharePoint } from '../../sharepoint-integration/composables/sharePoint.js';

let _isInitialized = false;
let _options = null;
let _registration = null;
let _deferredPrompt = null;
let _state = null;
let _unsubscribeVersion = null;

const DEFAULT_OPTIONS = {
  swUrl: 'sw.js',
  scope: undefined,
  versionFileUrl: 'version.conf',
  versionPollIntervalMs: 30 * 60 * 1000,
  // Optional override for the SharePoint folder that hosts the deploy.
  // When unset the composable derives it from window.location and the
  // SharePoint client's siteUrl.
  rootPath: undefined,
};

// Marker added to the polling URL so the SW (and any HTTP intermediary)
// resolves it to a fresh response instead of the precached version.conf.
const NO_CACHE_MARKER = 'no_cache=1';

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  if (typeof navigator !== 'undefined' && navigator.standalone === true) {
    return true;
  }
  if (window.matchMedia) {
    return window.matchMedia('(display-mode: standalone)').matches;
  }
  return false;
}

function deriveRootPath(client) {
  if (!client || !client.siteUrl || typeof window === 'undefined') return '';
  try {
    const sitePath = new URL(client.siteUrl).pathname.replace(/\/+$/, '');
    const pagePath = new URL(window.location.href).pathname;
    if (!sitePath || !pagePath.startsWith(sitePath + '/')) return '';
    const remainder = pagePath.slice(sitePath.length + 1);
    const lastSlash = remainder.lastIndexOf('/');
    return lastSlash >= 0 ? remainder.slice(0, lastSlash) : '';
  } catch (_) {
    return '';
  }
}

function withNoCache(url) {
  return url.includes('?')
    ? `${url}&${NO_CACHE_MARKER}`
    : `${url}?${NO_CACHE_MARKER}`;
}

function ensureState() {
  if (_state) return _state;
  // Flat reactive object — Vue 3 reactive() auto-unwraps refs at the top
  // level of its proxy, so wrapping each field in ref() then reading
  // `_state.field.value` would throw (the proxy returns the unwrapped
  // primitive). Plain values + reactive() give the same reactivity with
  // a less surprising shape.
  _state = reactive({
    // Running — what the user is executing right now.
    currentVersion: null,
    currentCommit: null,
    currentDeployedAt: null,
    // Available — latest known on the server.
    availableVersion: null,
    availableCommit: null,
    availableDeployedAt: null,

    swEnabled: !!(_options && _options.swUrl),
    swActive: false,
    isInstalled: detectStandalone(),
    isInstallable: false,
    updateAvailable: false,
    installingUpdate: false,
    needsReloadForInstall: false,
    isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
  });
  return _state;
}

function parseSuccessPayload(text) {
  let v;
  try {
    v = JSON.parse(text);
  } catch (err) {
    console.warn('PWA: failed to parse version file:', err.message);
    return null;
  }
  if (v.status !== 'SUCCESS') {
    // Ignore STARTED / FAILURE — the deploy is in flight or broken.
    return null;
  }
  return v;
}

function applyRunningPayload(text) {
  const v = parseSuccessPayload(text);
  if (!v) return;
  _state.currentVersion = v.version ?? null;
  _state.currentCommit = v.commit ?? null;
  _state.currentDeployedAt = v.deployed_at ?? null;
  // If the available refs were never seeded, mirror the running version
  // so the UI does not show a phantom "update available" state during
  // the first poll round-trip.
  if (_state.availableCommit === null) {
    _state.availableVersion = v.version ?? null;
    _state.availableCommit = v.commit ?? null;
    _state.availableDeployedAt = v.deployed_at ?? null;
  }
  recomputeUpdateAvailable();
}

function applyAvailablePayload(text) {
  const v = parseSuccessPayload(text);
  if (!v) return;
  _state.availableVersion = v.version ?? null;
  _state.availableCommit = v.commit ?? null;
  _state.availableDeployedAt = v.deployed_at ?? null;
  recomputeUpdateAvailable();
  if (
    _state.updateAvailable &&
    _registration &&
    typeof _registration.update === 'function'
  ) {
    // Nudge the browser to refetch the SW. Because the new deploy
    // wrote a fresh sw.js (different bytes — new revision and file
    // list), the byte comparison detects the update and the install
    // handler runs against the new precache manifest.
    _registration.update().catch(err => {
      console.warn('PWA: registration.update() failed:', err.message);
    });
  }
}

function recomputeUpdateAvailable() {
  const cur = _state.currentCommit;
  const avail = _state.availableCommit;
  _state.updateAvailable =
    typeof cur === 'string' && typeof avail === 'string' && cur !== avail;
}

function captureInstallPromptEarly() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    _deferredPrompt = event;
    _state.isInstallable = true;
  });
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    _state.isInstallable = false;
    _state.isInstalled = true;
  });
}

function wireOnlineOffline() {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    _state.isOffline = false;
  });
  window.addEventListener('offline', () => {
    _state.isOffline = true;
  });
}

async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn(
      'PWA: service workers are not supported (or insecure context); ' +
        'continuing with version polling only.'
    );
    return;
  }

  const wasControlled = navigator.serviceWorker.controller !== null;
  const isStandalone = detectStandalone();

  const registerOptions = _options.scope ? { scope: _options.scope } : {};

  let registration;
  try {
    registration = await navigator.serviceWorker.register(
      _options.swUrl,
      registerOptions
    );
  } catch (err) {
    // Per project rules: fail loud on a supported browser.
    throw new Error(`PWA: service worker registration failed — ${err.message}`);
  }

  _registration = registration;
  _state.swActive = navigator.serviceWorker.controller !== null;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    _state.swActive = true;
    if (!wasControlled && !isStandalone) {
      _state.needsReloadForInstall = true;
    }
  });

  // updateAvailable is driven exclusively by commit drift between the
  // cached version.conf (running) and the network version.conf
  // (available). A waiting SW with the *same* commit (e.g. the bundled
  // airlift-sw.js bytes changed across an airlift release while the app
  // commit did not) must not surface as an update — the user sees the
  // same version on both sides and is rightly confused.
  //
  // installingUpdate, by contrast, mirrors the SW install lifecycle
  // regardless of commit: it's true while a fresh SW is between
  // `installing` and `installed`/`redundant`. The PwaButton uses it to
  // surface "Preparing update…" feedback so the user is not left
  // wondering why nothing happens when they click Update during the
  // precache window.
  if (registration.installing) trackInstalling(registration.installing);
  registration.addEventListener('updatefound', () => {
    if (registration.installing) trackInstalling(registration.installing);
  });
}

// Mirror an `installing` worker's state onto `_state.installingUpdate`.
// Cleans itself up once the worker leaves the `installing` state
// (transitions to `installed`, `activating`, `activated`, or
// `redundant`).
function trackInstalling(sw) {
  if (!sw || sw.state !== 'installing') return;
  _state.installingUpdate = true;
  const onStateChange = () => {
    if (sw.state === 'installing') return;
    _state.installingUpdate = false;
    sw.removeEventListener('statechange', onStateChange);
  };
  sw.addEventListener('statechange', onStateChange);
}

// Resolve once a waiting SW is available, or null on failure/timeout.
// Used by applyUpdate() when the user clicks Update before the
// browser's own update check has produced a waiting worker — calling
// registration.update() and awaiting the install gives the click a
// chance to actually do something.
function waitForWaitingWorker(registration, timeoutMs) {
  return new Promise(resolve => {
    if (registration.waiting) {
      resolve(registration.waiting);
      return;
    }
    let settled = false;
    let timeoutId = null;
    const settle = value => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      registration.removeEventListener('updatefound', onUpdatefound);
      resolve(value);
    };
    const watchInstalling = sw => {
      if (!sw) return;
      const onStateChange = () => {
        if (sw.state === 'installed') {
          settle(registration.waiting || null);
        } else if (sw.state === 'redundant') {
          settle(null);
        }
      };
      sw.addEventListener('statechange', onStateChange);
    };
    const onUpdatefound = () => watchInstalling(registration.installing);
    registration.addEventListener('updatefound', onUpdatefound);
    watchInstalling(registration.installing);
    timeoutId = setTimeout(
      () => settle(registration.waiting || null),
      timeoutMs
    );
  });
}

function getSharePointFilesIfReady() {
  // Use the SharePoint integration's client when available — that gives
  // us auth-aware fetches against the right siteUrl/rootPath. We do NOT
  // await ensureInitialized() here: if the host app never called
  // initSharePoint(), the promise never resolves and polling would hang.
  const sharePoint = useSharePoint();
  const client = sharePoint && sharePoint.client && sharePoint.client.value;
  if (!client) return null;
  const rootPath =
    _options.rootPath !== undefined
      ? _options.rootPath
      : deriveRootPath(client);
  return client.files({ rootPath });
}

// One-shot fetch of the *running* version.conf — must hit the SW cache
// when present so the value reflects what was actually precached. Goes
// straight through the page's fetch (no SP integration) so the SW sees
// the request and can serve from cache.
async function fetchRunningVersion() {
  if (typeof fetch === 'undefined') return;
  try {
    const response = await fetch(_options.versionFileUrl, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(
        `PWA: ${_options.versionFileUrl} returned ${response.status}`
      );
    }
    const text = await response.text();
    applyRunningPayload(text);
  } catch (err) {
    // Running version is best-effort: if we cannot read it (offline on
    // the very first visit, SW not yet active), the available version
    // takes over via the poller.
    console.warn('PWA: failed to read running version:', err.message);
  }
}

async function pollAvailableOnce() {
  const pollUrl = withNoCache(_options.versionFileUrl);
  const files = getSharePointFilesIfReady();
  if (files) {
    const result = await files.downloadWithETag(pollUrl);
    if (result && typeof result.data === 'string') {
      applyAvailablePayload(result.data);
    }
    return;
  }
  // Fallback: plain page-relative fetch with credentials. Works on
  // localhost and on apps that have not wired the SP integration.
  const response = await fetch(pollUrl, {
    credentials: 'include',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`PWA: ${pollUrl} returned ${response.status}`);
  }
  const text = await response.text();
  applyAvailablePayload(text);
}

function startVersionPolling() {
  const pollUrl = withNoCache(_options.versionFileUrl);
  const files = getSharePointFilesIfReady();
  if (files) {
    _unsubscribeVersion = files.onFileChange(
      pollUrl,
      payload => {
        if (payload && typeof payload.data === 'string') {
          applyAvailablePayload(payload.data);
        }
      },
      {
        intervalMs: _options.versionPollIntervalMs,
        onVisibility: true,
        onOnline: true,
      }
    );
    return;
  }
  // Fallback poller — same trigger schedule but plain fetch.
  startFallbackPoller(pollUrl);
}

function startFallbackPoller(pollUrl) {
  let lastEtag = null;
  let stopped = false;
  let inFlight = false;

  const tick = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    try {
      const headers = {};
      if (lastEtag) headers['If-None-Match'] = lastEtag;
      const response = await fetch(pollUrl, {
        method: 'GET',
        headers,
        credentials: 'include',
        cache: 'no-store',
      });
      if (stopped) return;
      if (response.status === 304) return;
      if (!response.ok) {
        throw new Error(`PWA: ${pollUrl} returned ${response.status}`);
      }
      const etag = response.headers.get('ETag');
      const text = await response.text();
      lastEtag = etag ?? lastEtag;
      applyAvailablePayload(text);
    } catch (err) {
      console.warn('PWA: version poll tick failed:', err.message);
    } finally {
      inFlight = false;
    }
  };

  const interval = _options.versionPollIntervalMs;
  let timerId = null;
  if (interval > 0) timerId = setInterval(tick, interval);

  const visibilityHandler = () => {
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible'
    ) {
      tick();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  const onlineHandler = () => tick();
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onlineHandler);
  }

  tick();

  _unsubscribeVersion = () => {
    stopped = true;
    if (timerId !== null) clearInterval(timerId);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onlineHandler);
    }
  };
}

/**
 * Initialize the PWA layer. Call once at startup, synchronously, before
 * any other async work — `beforeinstallprompt` is captured on the very
 * first line so it is not missed.
 *
 * @param {object} [options={}]
 * @param {string} [options.swUrl='sw.js']
 * @param {string} [options.scope]
 * @param {string} [options.versionFileUrl='version.conf']
 * @param {number} [options.versionPollIntervalMs=1800000]
 * @param {string} [options.rootPath]
 */
export function initPwa(options = {}) {
  if (_isInitialized) {
    throw new Error(
      'initPwa: already initialized. Use usePwa() to access the instance.'
    );
  }
  _isInitialized = true;
  _options = { ...DEFAULT_OPTIONS, ...options };

  ensureState();
  wireOnlineOffline();

  if (_options.swUrl) {
    // Capture the install prompt before any async work to minimise the
    // race window where the event could fire and be lost.
    captureInstallPromptEarly();
    // SW registration uses the bare swUrl: the deploy-generated sw.js
    // bytes change between deploys, so the browser's standard byte-diff
    // update detection does the right thing without URL-level trickery.
    registerServiceWorker().catch(err => {
      // Already loud (throws); log so it is visible at startup.
      console.error(err);
    });
  }
  fetchRunningVersion();
  startVersionPolling();
}

async function install() {
  if (!_deferredPrompt) return 'unavailable';
  try {
    _deferredPrompt.prompt();
    const choice = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    _state.isInstallable = false;
    return choice && choice.outcome ? choice.outcome : 'dismissed';
  } catch (err) {
    console.warn('PWA: install prompt failed:', err.message);
    _deferredPrompt = null;
    _state.isInstallable = false;
    return 'dismissed';
  }
}

// Time budget for the "click Update before precache finishes" path.
// Most precaches complete within a few seconds; 60s covers slow links
// without leaving the spinner spinning indefinitely.
const INSTALL_WAIT_TIMEOUT_MS = 60 * 1000;

async function applyUpdate() {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !_registration
  ) {
    return;
  }
  // If no waiting worker, the user clicked before precache finished
  // (or before the browser even noticed the new sw.js). Kick the
  // update and wait for the install to produce a waiting worker.
  // A bare reload is intentionally avoided: it would navigate through
  // the still-active old SW and serve the same cached bundle, which
  // is exactly the "no effective update" failure mode this guards
  // against.
  if (!_registration.waiting) {
    _state.installingUpdate = true;
    try {
      await _registration.update();
    } catch (err) {
      console.warn('PWA: registration.update() failed:', err.message);
    }
    const waiting = await waitForWaitingWorker(
      _registration,
      INSTALL_WAIT_TIMEOUT_MS
    );
    _state.installingUpdate = false;
    if (!waiting) {
      console.warn(
        'PWA: no waiting service worker after update — install may have failed or timed out.'
      );
      return;
    }
  }
  // Wait for controllerchange before reloading so the page does not race
  // the SW takeover.
  const controllerChanged = new Promise(resolve => {
    const handler = () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handler);
      resolve();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handler);
  });
  _registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  await controllerChanged;
  if (typeof window !== 'undefined') window.location.reload();
}

async function refreshVersion() {
  await pollAvailableOnce();
}

/**
 * Reactive PWA composable. Requires `initPwa()` to have been called.
 *
 * @returns {object} reactive state and actions
 */
export function usePwa() {
  if (!_isInitialized) {
    throw new Error('usePwa: initPwa() must be called before usePwa().');
  }
  return reactive({
    currentVersion: computed(() => _state.currentVersion),
    currentCommit: computed(() => _state.currentCommit),
    currentDeployedAt: computed(() => _state.currentDeployedAt),
    availableVersion: computed(() => _state.availableVersion),
    availableCommit: computed(() => _state.availableCommit),
    availableDeployedAt: computed(() => _state.availableDeployedAt),
    swEnabled: computed(() => _state.swEnabled),
    swActive: computed(() => _state.swActive),
    isInstalled: computed(() => _state.isInstalled),
    isInstallable: computed(() => _state.isInstallable),
    updateAvailable: computed(() => _state.updateAvailable),
    installingUpdate: computed(() => _state.installingUpdate),
    needsReloadForInstall: computed(() => _state.needsReloadForInstall),
    isOffline: computed(() => _state.isOffline),
    install,
    applyUpdate,
    refreshVersion,
  });
}

// Test-only: reset module state so tests can exercise initPwa repeatedly.
export function _resetPwaForTests() {
  _isInitialized = false;
  _options = null;
  _registration = null;
  _deferredPrompt = null;
  _state = null;
  if (_unsubscribeVersion) {
    try {
      _unsubscribeVersion();
    } catch (_) {
      // ignore
    }
    _unsubscribeVersion = null;
  }
}
